/**
 * PAGE-LEVEL FAQS — one source for the rendered accordion AND the FAQPage
 * schema.
 *
 * These lived inline in the page components, which meant the visible Q&A and
 * the emitted JSON-LD could never come from the same place — so the schema
 * simply did not exist. The recovered AI-SEO research measured the gap
 * directly: /for-consultants renders five real procurement questions and
 * /pricing renders nine buyer questions, and neither page emitted FAQPage
 * markup, on the two pages where a quotable answer is worth the most.
 *
 * Google requires that FAQ schema mirror visible content. Keeping both
 * consumers on this module makes drift impossible.
 */
import { COVERAGE } from "./business";

export interface PageFAQ {
  question: string;
  answer: string;
}

export const CONSULTANT_FAQS: PageFAQ[] = [
  {
    question: "Can a consultancy subcontract aerial capture for a client engagement?",
    answer:
      "Yes. That is a standard arrangement. Work is quoted against your written scope, delivered to your specification, and the site imagery is treated as client-confidential unless release is agreed. Non-disclosure agreements are accepted on your paper or ours.",
  },
  {
    question: "Who owns the footage from a commissioned aerial shoot?",
    answer:
      "Ownership and usage are agreed in the engagement scope before the shoot, not afterwards. Raw files are available to the client on request rather than withheld as leverage, which is the point at which most aerial vendor relationships go wrong.",
  },
  {
    question: "Can one engagement cover sites in more than one province?",
    answer: `Yes. ${COVERAGE.provincesWorked.join(" and ")} are home territory mobilised from Calgary, with confirmed prior work across Canada. International sites are covered on a client-funded travel basis under the same engagement.`,
  },
  {
    question: "What certification does commercial aerial work in Canada require?",
    answer:
      "A Transport Canada RPAS Pilot Certificate. Advanced Operations is the level required to fly in controlled airspace or near bystanders — which covers most of urban Alberta. Fly4MEdia holds Advanced.",
  },
  {
    question: "How is a multi-site programme kept consistent over time?",
    answer:
      "Capture points are recorded as fixed coordinates, altitude and heading, and flight logs are retained per sortie. The frame shot in month nine matches the frame shot in month one, which is what makes a sequence usable as a record rather than a set of photographs.",
  },
];

/**
 * The pricing page's buyer questions — moved verbatim from PricingFAQ.tsx so
 * the rendered accordion and the FAQPage schema share one source. These
 * contain the page's own visible price claims and mirror them exactly.
 */
export const PRICING_FAQS: PageFAQ[] = [
  {
    question: "I've never hired drone footage before. How do I know which package is right for me?",
    answer: "Start with where the footage will live. MLS listing only — Essential covers everything you need. Multiple platforms including social and your website — Elevated gives you all the formats. A flagship asset you'll use for 12 months or more — Signature is the right investment. If you're still unsure, the best 15 minutes you can spend is a call before booking. We'll work out exactly what you need — and what you don't — before any money changes hands. Book a free call through the contact form.",
  },
  {
    question: "What's the actual difference between this and a $200 drone operator?",
    answer: "A $200 operator delivers raw files from ungraded footage. You edit, colour correct, add music, and compress for platforms yourself — or you don't, and the footage sits unused. Every Fly4MEdia package delivers a finished asset: colour graded, music licensed, exported for every platform it needs to live on, and in your hands within 48 hours. The music licence matters too — unlicensed tracks get listing videos muted on YouTube and flagged on social. Every package includes music cleared for MLS, social, and web. The difference you're paying for is the gap between a hard drive of clips and a finished asset you can publish the day you receive it.",
  },
  {
    question: "What does the brief process actually involve?",
    answer: "A 20-minute call or a written brief you send by email — whichever you prefer. We cover: the location, the feeling you want the footage to produce, where it will be used (MLS, Instagram, website, boardroom presentation), and any specific shots you have in mind. You don't need a production background. The brief is what makes the delivered footage match what you needed — and what makes the reshoot guarantee rarely apply.",
  },
  {
    question: "What happens if weather cancels the shoot?",
    answer: "We reschedule, not refund. Your deposit holds your spot on the calendar, not a specific date. If conditions on the shoot day won't produce footage worth delivering — high winds, poor visibility, rain — we call it and reschedule at no cost. You're never charged for a shoot that didn't happen. Most bookings are weather-confirmed 48 hours in advance.",
  },
  {
    question: "Can I request a specific time of day — sunrise, golden hour, or sunset?",
    answer: "Yes. Signature packages include a golden-hour or sunset window by default. Essential and Elevated packages can request preferred timing and we schedule around it where conditions allow. If you have a specific light condition in mind — overcast for even colour, sunrise for a particular sky — mention it in the brief. It usually doesn't add cost.",
  },
  {
    question: "Do you handle editing or is this raw footage?",
    answer: "Everything is edited and delivered as a finished asset. You receive colour-graded video, licensed music embedded, exported for every platform you need. Elevated and Signature packages also include the raw selects so you have source material for future use. You receive a finished deliverable, not a hard drive full of clips.",
  },
  {
    question: "Can I upgrade my package after booking?",
    answer: "Yes. If your scope expands — you want to add the ground walkthrough, more photographs, or a vertical cut — we re-quote before the shoot day. No surprise invoices. If it becomes clear on the day that something extra is worth capturing, we'll tell you before we shoot it.",
  },
  {
    question: "Do you travel outside Calgary and Alberta?",
    answer: "Travel within Alberta is included in all packages — no fuel surcharges, no travel fees within the province. For projects outside Alberta, travel is quoted upfront as a flat line item before booking is confirmed. Current rate: $1.20 per km. It's in the add-ons section above. No surprises after the fact.",
  },
  {
    question: "How many projects do you take on per month?",
    answer: "Intentionally limited. Parker operates alone — there's no crew of operators dispatched under a brand name. Keeping project volume controlled is how quality stays consistent. When the calendar fills, it fills. If you have a date-sensitive project — a listing going live in two weeks, a venue with a peak inquiry season — book early and lock the date.",
  },
];
